import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Square, Loader2, Play, Pause, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VoiceNoteRecorder — reusable admin voice-note capture with auto-transcription.
 *
 * Props:
 *   value:  { audio_url, transcript } | null
 *   onChange: (next: { audio_url, transcript } | null) => void
 *   placeholder: string (helper text)
 *
 * On record → upload → Whisper transcribes → onChange emits { audio_url, transcript }.
 * User can also just type the transcript directly if they don't want to record.
 */
export const VoiceNoteRecorder = ({ value, onChange, placeholder }) => {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const timerRef = useRef(null);
  const audioElRef = useRef(null);

  const transcript = value?.transcript || "";
  const audioUrl = value?.audio_url ? (value.audio_url.startsWith("http") ? value.audio_url : `${process.env.REACT_APP_BACKEND_URL}${value.audio_url}`) : "";

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Your browser doesn't support voice recording.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setAudioBlob(blob);
        setRecording(false);
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        await uploadForTranscription(blob, mr.mimeType || "audio/webm");
      };
      mr.start();
      mediaRecorderRef.current = mr;
      startTsRef.current = Date.now();
      setDuration(0);
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTsRef.current) / 1000));
      }, 250);
      setRecording(true);
    } catch (err) {
      const name = err?.name;
      if (name === "NotAllowedError") toast.error("Microphone permission denied.");
      else toast.error("Could not start recording.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  const uploadForTranscription = async (blob, mime) => {
    setUploading(true);
    try {
      const form = new FormData();
      const ext = mime.includes("webm") ? "webm" : mime.includes("mp4") ? "m4a" : mime.includes("wav") ? "wav" : "webm";
      form.append("file", new File([blob], `voice-note.${ext}`, { type: mime }));
      const { data } = await axios.post(`${API}/admin/transcribe`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      onChange({ audio_url: data.audio_url, transcript: data.transcript || transcript });
      if (data.transcribed) {
        toast.success("Voice note transcribed.");
      } else {
        toast.warning("Audio saved but transcription failed — you can type the text below.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const togglePlay = () => {
    if (!audioElRef.current) return;
    if (playing) audioElRef.current.pause();
    else audioElRef.current.play();
  };

  const clearAll = () => {
    setAudioBlob(null);
    setDuration(0);
    onChange(null);
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="space-y-2" data-testid="voice-note-recorder">
      <div className="flex items-center gap-3 flex-wrap">
        {!recording && !uploading && !audioUrl && (
          <button
            type="button"
            onClick={startRecording}
            className="inline-flex items-center gap-2 rounded-full px-4 h-9 border border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white transition-colors text-xs uppercase tracking-[0.14em] font-brand"
            data-testid="voice-record-start"
          >
            <Mic size={14} /> Record voice note
          </button>
        )}
        {recording && (
          <button
            type="button"
            onClick={stopRecording}
            className="inline-flex items-center gap-2 rounded-full px-4 h-9 bg-[#8C2E39] text-white text-xs uppercase tracking-[0.14em] font-brand animate-pulse"
            data-testid="voice-record-stop"
          >
            <Square size={12} /> Stop · {fmt(duration)}
          </button>
        )}
        {uploading && (
          <span className="inline-flex items-center gap-2 rounded-full px-4 h-9 bg-[#F5EFE6] text-[#6A5F58] text-xs uppercase tracking-[0.14em]">
            <Loader2 size={12} className="animate-spin" /> Transcribing…
          </span>
        )}
        {audioUrl && !recording && !uploading && (
          <>
            <button
              type="button"
              onClick={togglePlay}
              className="inline-flex items-center gap-2 rounded-full px-3 h-9 border border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6] transition-colors text-xs uppercase tracking-[0.14em]"
              data-testid="voice-play"
            >
              {playing ? <Pause size={12} /> : <Play size={12} />}
              {playing ? "Pause" : "Play"}
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 text-[#8C2E39] hover:underline text-[11px] uppercase tracking-[0.14em]"
              data-testid="voice-clear"
            >
              <Trash2 size={12} /> Remove
            </button>
            <audio
              ref={audioElRef}
              src={audioUrl}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              preload="metadata"
              className="hidden"
            />
          </>
        )}
      </div>

      {/* Editable transcript field */}
      <textarea
        value={transcript}
        onChange={(e) => onChange({ ...(value || {}), transcript: e.target.value })}
        placeholder={placeholder || "Notes appear here after recording, or type directly."}
        rows={3}
        maxLength={2000}
        className="w-full rounded-lg border border-[#DDD1BE] bg-[#F5EFE6] px-3 py-2 text-sm text-[#14100F] focus:outline-none focus:ring-2 focus:ring-[#B76E79] resize-none"
        data-testid="voice-note-text"
      />
      <p className="text-[10px] text-[#6A5F58]">
        {transcript.length}/2000 · admin only, never shown to guests
      </p>
    </div>
  );
};

export default VoiceNoteRecorder;
